import { useEffect, useMemo, useState } from "react";
import { applicationsApi, ApiError } from "@/api";
import type { ApplicationKind, ApplicationCreateInput, AuthorityRow, DrawingSet } from "@/types";
import { PLANNING_SUBTYPES, BC_SUBTYPES } from "@/types";
import { Button, TextField, TextAreaField, SelectField } from "@/components/ui";
import { humanise, drawingCode, drawingStatusLabel, workstreamTypeLabel } from "@/lib/jobs";
import { FormDrawer } from "./FormDrawer";

const KINDS: { value: ApplicationKind; label: string }[] = [
  { value: "planning", label: "Planning" },
  { value: "building_control", label: "Building control" },
];

/** Create an application under the job (Brief 03 §3.2). `type` drives the
 *  authority filter (LPA vs BCB), the subtype suggestions, and (post-create) the
 *  status options. Blank reference is allowed; a duplicate non-blank ref is
 *  surfaced inline. */
export function CreateApplicationDrawer({
  jobId,
  jobNumber,
  defaultAddress,
  defaultPostcode,
  drawingSets,
  conservationArea = false,
  constraintsResolved = false,
  onClose,
  onSaved,
}: {
  jobId: number;
  jobNumber: number;
  defaultAddress: string;
  defaultPostcode: string;
  drawingSets: DrawingSet[];
  /** The site is in a confirmed conservation area (from job.constraints). When so,
   *  a CLOPUD / Prior Approval subtype gets a non-blocking warning nudging staff to
   *  a Householder application (permitted-development rights are restricted here). */
  conservationArea?: boolean;
  /** The job's planning constraints have been successfully looked up (lookup_ok).
   *  When they haven't, a CLOPUD / Prior Approval subtype gets a "not checked"
   *  warning — a conservation area we don't yet know about would rule that route out. */
  constraintsResolved?: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [kind, setKind] = useState<ApplicationKind>("planning");
  const [authority, setAuthority] = useState("");
  const [reference, setReference] = useState("");
  const [subtype, setSubtype] = useState("");
  const [address, setAddress] = useState(defaultAddress);
  const [postcode, setPostcode] = useState(defaultPostcode);
  const [isMajor, setIsMajor] = useState(false);
  const [description, setDescription] = useState("");
  // The drawing whose submit task this application fulfils (Addendum 2). 0 = none.
  const [drawingId, setDrawingId] = useState(0);

  const [authorities, setAuthorities] = useState<AuthorityRow[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | undefined>();

  // Drawings of the matching type across all sets — the candidates whose submit
  // task this application can close. `other` drawings have no application, so
  // they never match. Reset the choice when the planning/BC toggle flips.
  const matchingDrawings = useMemo(() => {
    const out: { id: number; label: string }[] = [];
    for (const set of drawingSets) {
      for (const d of set.drawings) {
        if (d.drawing_type !== kind) continue;
        const code = drawingCode(jobNumber, set.set_number, d.drawing_number);
        out.push({
          id: d.id,
          label: `${code} · ${workstreamTypeLabel(d.drawing_type)}${d.title ? ` · ${d.title}` : ""} · ${drawingStatusLabel(d.status)}`,
        });
      }
    }
    return out;
  }, [drawingSets, kind, jobNumber]);
  useEffect(() => {
    setDrawingId(0);
  }, [kind]);

  // Load the authority list for the selected kind (LPA for planning, BCB for
  // building control). Clear the chosen authority when the kind flips — the
  // lists differ.
  useEffect(() => {
    let cancelled = false;
    setAuthority("");
    applicationsApi
      .listAuthorities(kind === "planning" ? "lpa" : "bcb")
      .then((rows) => {
        if (!cancelled) setAuthorities(rows);
      })
      .catch(() => {
        if (!cancelled) setAuthorities([]);
      });
    return () => {
      cancelled = true;
    };
  }, [kind]);

  const subtypeOptions = kind === "planning" ? PLANNING_SUBTYPES : BC_SUBTYPES;
  // CLOPUD (a Lawful Development Certificate) and Prior Approval both lean on
  // permitted-development rights that a conservation area curtails. Two warn-only
  // nudges (both non-blocking): a strong one when the site is a confirmed
  // conservation area, and a softer "not checked yet" one when we don't have a
  // resolved constraints lookup — an unknown conservation area would rule the route out.
  const subtypeKey = subtype.trim().toLowerCase();
  const isPdReliantSubtype =
    kind === "planning" && (subtypeKey === "clopud" || subtypeKey === "prior_approval");
  const routeLabel = subtypeKey === "clopud" ? "CLOPUD (Lawful Development Certificate)" : "Prior Approval";
  const showConservationWarning = isPdReliantSubtype && conservationArea;
  const showUncheckedWarning = isPdReliantSubtype && !conservationArea && !constraintsResolved;

  async function submit() {
    if (saving) return;
    if (!authority.trim()) {
      setError(kind === "planning" ? "Choose the local planning authority." : "Choose the building control body.");
      return;
    }
    if (!address.trim() || !postcode.trim()) {
      setError("Enter the site address and postcode.");
      return;
    }
    if (!subtype.trim()) {
      setError("Choose the application subtype.");
      return;
    }
    setSaving(true);
    setError(undefined);
    const input: ApplicationCreateInput = {
      job_id: jobId,
      type: kind,
      local_authority_or_bcb: authority.trim(),
      site_address: address.trim(),
      site_postcode: postcode.trim(),
      app_reference: reference.trim() || undefined,
      application_type: subtype.trim(),
      description: description.trim() || undefined,
      is_major: kind === "planning" ? isMajor : undefined,
      drawing_id: drawingId > 0 ? drawingId : undefined,
    };
    try {
      await applicationsApi.createApplication(input);
      onSaved();
    } catch (err) {
      if (err instanceof ApiError && err.status === 400 && reference.trim()) {
        setError(`An application with reference ${reference.trim()} already exists for ${authority.trim()}.`);
      } else {
        setError(err instanceof ApiError ? err.message : "Could not create the application.");
      }
      setSaving(false);
    }
  }

  return (
    <FormDrawer
      title="New application"
      busy={saving}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" onClick={submit} loading={saving}>Create application</Button>
        </>
      }
    >
      {error ? <p className="leads__error" role="alert" onClick={() => setError(undefined)}>{error}</p> : null}

      <section className="job-drawer__section">
        <h3>Type</h3>
        <div className="job-seg" role="tablist" aria-label="Application type">
          {KINDS.map((k) => (
            <button
              key={k.value}
              type="button"
              role="tab"
              aria-selected={kind === k.value}
              className={`job-seg__btn${kind === k.value ? " is-on" : ""}`}
              onClick={() => { setKind(k.value); setError(undefined); }}
            >
              {k.label}
            </button>
          ))}
        </div>

        <TextField
          label={kind === "planning" ? "Local planning authority" : "Building control body"}
          required
          list="app-authority-list"
          value={authority}
          onChange={(e) => setAuthority(e.target.value)}
          placeholder="Start typing to search…"
          hint="Pick from the list, or type a name if it isn't listed."
        />
        <datalist id="app-authority-list">
          {authorities.map((a) => (
            <option key={a.id} value={a.canonical_name} />
          ))}
        </datalist>

        <TextField
          label="Reference (optional)"
          value={reference}
          onChange={(e) => setReference(e.target.value)}
          hint="Leave blank if not yet allocated — it'll be matched automatically when the first letter arrives."
        />

        <TextField
          label="Subtype"
          required
          list="app-subtype-list"
          value={subtype}
          onChange={(e) => setSubtype(e.target.value)}
          placeholder="e.g. householder"
        />
        <datalist id="app-subtype-list">
          {subtypeOptions.map((s) => (
            <option key={s} value={s}>{humanise(s)}</option>
          ))}
        </datalist>

        {showConservationWarning ? (
          <p className="create-app__warn" role="status">
            This site is in a <strong>conservation area</strong>, where permitted-development rights are restricted —
            a {routeLabel} may not be valid. Consider a <strong>Householder</strong> application instead.
          </p>
        ) : showUncheckedWarning ? (
          <p className="create-app__warn" role="status">
            Site constraints haven’t been checked for this job — an undetected <strong>conservation area</strong> would
            restrict permitted-development rights and could rule out a {routeLabel}. Check planning constraints on the
            job first, or consider a <strong>Householder</strong> application.
          </p>
        ) : null}

        {kind === "planning" ? (
          <label className="lead-drawer__check">
            <input type="checkbox" checked={isMajor} onChange={(e) => setIsMajor(e.target.checked)} />
            Major application
          </label>
        ) : null}

        {matchingDrawings.length > 0 ? (
          <SelectField
            label="Drawing (submit task)"
            value={String(drawingId)}
            onChange={(e) => setDrawingId(Number(e.target.value))}
            hint="Link the drawing this application is for, so submitting it closes that drawing's submit task."
          >
            <option value="0">— none —</option>
            {matchingDrawings.map((d) => (
              <option key={d.id} value={d.id}>{d.label}</option>
            ))}
          </SelectField>
        ) : (
          <SelectField
            label="Drawing (submit task)"
            value="0"
            disabled
            hint={`No ${kind === "planning" ? "planning" : "building control"} drawings yet — create one in the Drawing sets tab.`}
          >
            <option value="0">— none —</option>
          </SelectField>
        )}
      </section>

      <section className="job-drawer__section">
        <h3>Site</h3>
        <TextField label="Site address" required value={address} onChange={(e) => setAddress(e.target.value)} />
        <TextField label="Site postcode" required value={postcode} onChange={(e) => setPostcode(e.target.value.toUpperCase())} />
      </section>

      <section className="job-drawer__section">
        <h3>Description</h3>
        <TextAreaField label="Description (optional)" value={description} onChange={(e) => setDescription(e.target.value)} />
      </section>
    </FormDrawer>
  );
}
